import Stack from '@mui/material/Stack';
import { useAskResearch } from '@/features/assistant';
import { PagedSection } from '@/shared/components/PagedSection';
import { PageHeader } from '@/shared/components/PageHeader';
import { SearchBox } from '@/shared/components/SearchBox';
import { useListUrlState } from '@/shared/hooks/useListUrlState';
import { formatDate, pluralise } from '@/shared/lib/format';
import type { Facets, LibraryParams } from '../api/libraryApi';
import { LibraryFilterRow } from '../components/LibraryFilterRow';
import { NoResults } from '../components/NoResults';
import { ReportGrid } from '../components/ReportGrid';
import { ResultsBar } from '../components/ResultsBar';
import { useDocumentsQuery, useFacetsQuery } from '../hooks/useLibraryQueries';
import { fromForRange, joinList, noMatchReason, splitList } from '../model/filters';

const DEFAULTS = { pageSize: 25, filters: { q: '', assetClass: '', currency: '', analyst: '', range: '', sort: '' } };

const describe = (coverage: Facets['coverage'] | undefined) =>
  coverage ? `${pluralise(coverage.total, 'public OCBC report')} from ${formatDate(coverage.from)} to ${formatDate(coverage.to)}.` : 'Public OCBC Global Markets Research.';

export function LibraryPage() {
  const list = useListUrlState(DEFAULTS);
  const facets = useFacetsQuery();
  const askResearch = useAskResearch();
  const { q, assetClass, currency, analyst, range, sort } = list.filters;
  const analysts = splitList(analyst);
  const params: LibraryParams = { page: list.page, pageSize: list.pageSize, q, assetClass, currency, analyst, sort, from: fromForRange(range, facets.data?.coverage.to), to: '' };
  const query = useDocumentsQuery(params);
  const newestFirst = !q || sort === 'newest';
  const clear = () => { list.setFilters({ assetClass: '', currency: '', analyst: '', range: '' }); };

  return (
    <>
      <PageHeader title="Research library" description={describe(facets.data?.coverage)} />
      <Stack sx={{ gap: 2 }}>
        <SearchBox
          value={q}
          onChange={(next) => { list.setFilters({ q: next }); }}
          label="Search research"
          placeholder="Search by title, instrument or analyst, e.g. latest USD/SGD view"
        />
        <LibraryFilterRow
          facets={facets.data}
          assetClass={assetClass}
          analysts={analysts}
          currency={currency}
          onAssetClass={(v) => { list.setFilters({ assetClass: v }); }}
          onAnalysts={(v) => { list.setFilters({ analyst: joinList(v) }); }}
          onCurrency={(v) => { list.setFilters({ currency: v }); }}
        />
        <ResultsBar
          total={query.data?.total}
          searching={q !== ''}
          newestFirst={newestFirst}
          range={range}
          onSort={(newest) => { list.setFilters({ sort: newest ? 'newest' : '' }); }}
          onRange={(v) => { list.setFilters({ range: v }); }}
        />
        {query.data?.total === 0 ? (
          <NoResults reason={noMatchReason({ q, assetClass, analysts, currency })} canAsk={q !== ''} onClear={clear} onAsk={() => { askResearch(q); }} />
        ) : (
          <PagedSection query={query} paging={list} empty={{ title: 'No reports match' }}>
            {(items, isLoading) => <ReportGrid items={items} isLoading={isLoading} query={q} />}
          </PagedSection>
        )}
      </Stack>
    </>
  );
}
